<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link view="AdminReimbursementList" label="Reimbursements" :active="true" />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Reimbursements</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} reimbursements</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button
              type="is-primary is-outlined"
              tag="router-link"
              icon-left="cloud-upload"
              :to="{ name: 'AdminReimbursementUpload' }"
            >
              Upload Reimbursements
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <b-field>
        <b-button
          icon-left="delete"
          type="is-danger"
          v-if="checkedRows.length"
          @click="handleBulkDelete"
        >
          Delete selected ({{ checkedRows.length }})
        </b-button>
      </b-field>

      <b-field>
        <div class="field-body">
          <b-input
            class="is-expanded"
            :loading="loading"
            v-model="search"
            placeholder="Search by Rev Code, Payer, Employer, Group Number, Prefix Code, or Claim ID"
            @input="debouncedSearch"
          />
          <b-dropdown
            class="filter-dropdown"
            position="is-bottom-left"
            v-model="searchableFields"
            multiple
            aria-role="list"
          >
            <template #trigger>
              <b-button icon-right="filter" type="is-primary">
                Search Fields ({{ searchableFields.length }})
              </b-button>
            </template>

            <b-dropdown-item
              v-for="field in allFields"
              :key="field.value"
              :value="field.value"
              aria-role="listitem"
            >
              <div @click="toggleCheckbox(field.value)">
                <b-checkbox
                  v-model="searchableFields"
                  :native-value="field.value"
                  class="checkbox-left"
                >
                  {{ field.label }}
                </b-checkbox>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="reimbursements"
          :loading="loading"
          detailed
          detail-key="id"
          :show-detail-icon="true"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="id"
          default-sort-direction="desc"
          checkable
          :checked-rows.sync="checkedRows"
        >
          <!-- Main table columns - showing only key information -->
          <b-table-column field="revenue_code" label="Rev Code" width="120" v-slot="props">
            <span class="is-family-monospace has-text-weight-semibold">
              {{ props.row.revenue_code }}
            </span>
          </b-table-column>

          <b-table-column field="payer_name" label="Payer" v-slot="props">
            <span :title="props.row.payer?.name">
              {{ truncateText(props.row.payer?.name, 30) || '-' }}
            </span>
          </b-table-column>
          <b-table-column field="prefix_code" label="Prefix" width="100" v-slot="props">
            <span :title="props.row.prefix?.prefix">
              {{ props.row.prefix?.prefix || '-' }}
            </span>
          </b-table-column>
          <b-table-column field="employer_name" label="Employer" v-slot="props">
            <span :title="props.row.employer?.name">
              {{ truncateText(props.row.employer?.name, 30) || '-' }}
            </span>
          </b-table-column>

          <b-table-column field="group_number" label="Group" v-slot="props">
            <span :title="props.row.group?.number">{{ props.row.group?.number || '-' }}</span>
          </b-table-column>

          <b-table-column field="percent_allowed_on_charges" label="% Allow" v-slot="props" numeric>
            <span class="has-text-weight-semibold has-text-success">
              {{ formatPercent(props.row.percent_allowed_on_charges) }}%
            </span>
          </b-table-column>

          <b-table-column field="created_at" label="Created At" v-slot="props" width="140">
            <span :title="formatDateFull(props.row.created_at)">
              {{ formatDate(props.row.created_at) }}
            </span>
          </b-table-column>

          <b-table-column label="Actions" width="100" v-slot="props">
            <b-button
              type="is-primary is-outlined"
              size="is-small"
              icon-left="square-edit-outline"
              @click="openEditModal(props.row)"
            >
              Edit
            </b-button>
          </b-table-column>

          <!-- Detailed row template for expanded content -->
          <template #detail="props">
            <p>
              <strong>Claim ID:</strong>
              {{ props.row.charge_claim_id }}
              <br />
              <strong>CPT Code:</strong>
              {{ props.row.charge_billed_cpt_code }}
              <br />
              <strong>Member ID:</strong>
              {{ props.row.primary_member_id }}
              <br />
              <strong>Prefix Code:</strong>
              {{ props.row.prefix?.prefix || '-' }}
              <br />
              <strong>Payment Date:</strong>
              {{ formatDate(props.row.payment_date) }}
              <br />
              <strong>Charge Amount:</strong>
              ${{ formatCurrency(props.row.charge_debit_amount) }}
              <br />
              <strong>Payment Amount:</strong>
              ${{ formatCurrency(props.row.payment_allowed_amount) }}
              <br />
              <strong>Percent Allowed:</strong>
              {{ formatPercent(props.row.percent_allowed_on_charges) }}%
            </p>
          </template>

          <template #empty>
            <div class="has-text-centered">
              <p>No reimbursements found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="600" scroll="keep">
        <form @submit.prevent="saveReimbursement">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit Reimbursement</p>
            </header>
            <section class="modal-card-body">
              <div class="columns is-multiline">
                <!-- Claim ID -->
                <div class="column is-half">
                  <b-field label="Charge Claim ID">
                    <b-input v-model="editForm.charge_claim_id" />
                  </b-field>
                </div>

                <!-- Revenue Code -->
                <div class="column is-half">
                  <b-field label="Revenue Code">
                    <b-input v-model="editForm.revenue_code" />
                  </b-field>
                </div>

                <!-- CPT Code -->
                <div class="column is-half">
                  <b-field label="CPT Code">
                    <b-input v-model="editForm.charge_billed_cpt_code" />
                  </b-field>
                </div>

                <!-- Member ID -->
                <div class="column is-half">
                  <b-field label="Primary Member ID">
                    <b-input v-model="editForm.primary_member_id" />
                  </b-field>
                </div>

                <!-- Payer -->
                <div class="column is-half">
                  <b-field label="Payer">
                    <b-autocomplete
                      v-model="editForm.payer_name"
                      :data="filteredPayerOptions"
                      field="name"
                      placeholder="Search or type payer name..."
                      open-on-focus
                      clearable
                      @select="handlePayerSelect"
                    />
                    <p v-if="isNewPayer" class="help is-size-7 new-entity-warning">
                      <b-icon icon="alert" size="is-small" class="mr-1"></b-icon>
                      New payer will be created
                    </p>
                  </b-field>
                </div>

                <!-- Employer -->
                <div class="column is-half">
                  <b-field label="Employer">
                    <b-autocomplete
                      v-model="editForm.employer_name"
                      :data="filteredEmployerOptions"
                      field="name"
                      placeholder="Search or type employer name..."
                      open-on-focus
                      clearable
                      @select="handleEmployerSelect"
                    />
                    <p v-if="isNewEmployer" class="help is-size-7 new-entity-warning">
                      <b-icon icon="alert" size="is-small" class="mr-1"></b-icon>
                      New employer will be created
                    </p>
                  </b-field>
                </div>

                <!-- Group -->
                <div class="column is-half">
                  <b-field label="Group">
                    <b-autocomplete
                      v-model="editForm.group_number"
                      :data="filteredGroupOptions"
                      field="number"
                      placeholder="Search or type group number..."
                      open-on-focus
                      clearable
                      @select="handleGroupSelect"
                    />
                    <p v-if="isNewGroup" class="help is-size-7 new-entity-warning">
                      <b-icon icon="alert" size="is-small" class="mr-1"></b-icon>
                      New group will be created
                    </p>
                  </b-field>
                </div>

                <!-- Prefix -->
                <div class="column is-half">
                  <b-field label="Prefix">
                    <b-autocomplete
                      v-model="editForm.prefix_code"
                      :data="filteredPrefixOptions"
                      field="prefix"
                      placeholder="Search or type prefix code..."
                      open-on-focus
                      clearable
                      @select="handlePrefixSelect"
                    />
                    <p v-if="isNewPrefix" class="help is-size-7 new-entity-warning">
                      <b-icon icon="alert" size="is-small" class="mr-1"></b-icon>
                      New prefix will be created
                    </p>
                  </b-field>
                </div>

                <!-- Payment Date -->
                <div class="column is-half">
                  <b-field label="Payment Date">
                    <b-datepicker
                      v-model="editForm.payment_date"
                      placeholder="Select date"
                      icon="calendar-today"
                      editable
                    />
                  </b-field>
                </div>

                <!-- Percent Allowed -->
                <div class="column is-half">
                  <b-field label="Percent Allowed on Charges">
                    <b-input
                      v-model="editForm.percent_allowed_on_charges"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                    />
                  </b-field>
                </div>

                <!-- Charge Debit Amount -->
                <div class="column is-half">
                  <b-field label="Charge Debit Amount">
                    <b-input
                      v-model="editForm.charge_debit_amount"
                      type="number"
                      step="0.01"
                      min="0"
                    />
                  </b-field>
                </div>

                <!-- Payment Allowed Amount -->
                <div class="column is-half">
                  <b-field label="Payment Allowed Amount">
                    <b-input
                      v-model="editForm.payment_allowed_amount"
                      type="number"
                      step="0.01"
                      min="0"
                    />
                  </b-field>
                </div>
              </div>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

export default {
  name: 'AdminReimbursementList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      reimbursements: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchableFields: [
        'revenue_code',
        'payer_name',
        'employer_name',
        'group_number',
        'prefix_code',
        'claim_id',
      ],
      allFields: [
        { label: 'Rev Code', value: 'revenue_code' },
        { label: 'Payer Name', value: 'payer_name' },
        { label: 'Employer Name', value: 'employer_name' },
        { label: 'Group Number', value: 'group_number' },
        { label: 'Prefix Code', value: 'prefix_code' },
        { label: 'Claim ID', value: 'claim_id' },
      ],
      searchTimeout: null,
      checkedRows: [],
      showEditModal: false,
      editLoading: false,
      editingReimbursement: null,
      editForm: {},
      payers: [],
      employers: [],
      groups: [],
      prefixes: [],
      // Options formatted for AjaxSelectField
      payerOptions: [],
      employerOptions: [],
      groupOptions: [],
      prefixOptions: [],
    };
  },
  created() {
    this.loadReimbursements();
    this.loadDropdownData();
  },
  computed: {
    filteredPayerOptions() {
      if (!this.editForm.payer_name) return this.payerOptions;
      return this.payerOptions.filter((option) =>
        option.name.toLowerCase().includes(this.editForm.payer_name.toLowerCase())
      );
    },
    filteredEmployerOptions() {
      if (!this.editForm.employer_name) return this.employerOptions;
      return this.employerOptions.filter((option) =>
        option.name.toLowerCase().includes(this.editForm.employer_name.toLowerCase())
      );
    },
    filteredGroupOptions() {
      if (!this.editForm.group_number) return this.groupOptions;
      return this.groupOptions.filter((option) =>
        option.number.toLowerCase().includes(this.editForm.group_number.toLowerCase())
      );
    },
    filteredPrefixOptions() {
      if (!this.editForm.prefix_code) return this.prefixOptions;
      return this.prefixOptions.filter((option) =>
        option.prefix.toLowerCase().includes(this.editForm.prefix_code.toLowerCase())
      );
    },
    isNewPayer() {
      if (
        !this.editForm.payer_name ||
        !this.editForm.payer_name.trim() ||
        this.editForm.payer_name === '-- No Payer --'
      )
        return false;

      const exists = this.payers.find(
        (p) => p.name.toLowerCase() === this.editForm.payer_name.toLowerCase()
      );
      return !exists;
    },
    isNewEmployer() {
      if (
        !this.editForm.employer_name ||
        !this.editForm.employer_name.trim() ||
        this.editForm.employer_name === '-- No Employer --'
      )
        return false;

      const exists = this.employers.find(
        (e) => e.name.toLowerCase() === this.editForm.employer_name.toLowerCase()
      );
      return !exists;
    },
    isNewGroup() {
      if (
        !this.editForm.group_number ||
        !this.editForm.group_number.trim() ||
        this.editForm.group_number === '-- No Group --'
      )
        return false;

      const exists = this.groups.find(
        (g) => g.number.toLowerCase() === this.editForm.group_number.toLowerCase()
      );
      return !exists;
    },
    isNewPrefix() {
      if (
        !this.editForm.prefix_code ||
        !this.editForm.prefix_code.trim() ||
        this.editForm.prefix_code === '-- No Prefix --'
      )
        return false;

      const exists = this.prefixes.find(
        (p) => p.prefix.toLowerCase() === this.editForm.prefix_code.toLowerCase()
      );
      return !exists;
    },
  },
  methods: {
    async loadReimbursements() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        params.append('limit', this.perPage);
        params.append('page', this.currentPage);

        // Add search parameters if they exist
        if (this.search && this.searchableFields && this.searchableFields.length > 0) {
          this.searchableFields.forEach((field) => {
            params.append(field, this.search.trim());
          });
        }

        const response = await request.get(`/admin/reimbursements?${params.toString()}`);
        this.reimbursements = response.data.list || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
        this.checkedRows = []; // Clear selected rows when data changes
      } catch (error) {
        console.error('Error loading reimbursements:', error);
        this.$buefy.toast.open({
          message: 'Failed to load reimbursements',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadReimbursements();
    },

    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1; // Reset to first page when searching
        this.loadReimbursements();
      }, 300);
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatDateShort(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    },

    formatDateFull(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    formatCurrency(amount) {
      if (amount == null || amount === '') return '0.00';
      return Number(amount).toFixed(2);
    },

    formatPercent(percent) {
      if (percent == null || percent === '') return '0.00';
      return Number(percent).toFixed(2);
    },

    truncateText(text, maxLength) {
      if (!text) return text;
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    toggleCheckbox(fieldValue) {
      // Toggle the checkbox when clicking anywhere in the dropdown item
      const index = this.searchableFields.indexOf(fieldValue);
      if (index > -1) {
        // Remove the field if it's already selected
        this.searchableFields.splice(index, 1);
      } else {
        // Add the field if it's not selected
        this.searchableFields.push(fieldValue);
      }
    },

    handleBulkDelete() {
      const count = this.checkedRows.length;
      let message = `Are you sure you want to delete ${count} reimbursement${count === 1 ? '' : 's'}?`;
      message += '<br/><br/><strong>This action cannot be undone.</strong>';

      this.$buefy.dialog.confirm({
        title: 'Delete Reimbursements',
        message: message,
        confirmText: `Delete ${count} reimbursement${count === 1 ? '' : 's'}`,
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.bulkDeleteReimbursements();
        },
      });
    },

    async bulkDeleteReimbursements() {
      try {
        this.loading = true;
        const ids = this.checkedRows.map((row) => row.id);

        const response = await request.delete('/admin/reimbursements', {
          data: { ids },
        });

        this.$buefy.toast.open({
          message:
            response.data.message || `Successfully deleted ${response.data.count} reimbursements`,
          type: 'is-success',
        });

        this.checkedRows = [];
        this.loadReimbursements(); // Refresh the list
      } catch (error) {
        console.error('Error deleting reimbursements:', error);
        this.$buefy.toast.open({
          message: 'Failed to delete reimbursements',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },

    async loadDropdownData() {
      try {
        // Load all dropdown data in parallel
        const [payersRes, employersRes, groupsRes, prefixesRes] = await Promise.all([
          request.get('/admin/reimbursements/payers'),
          request.get('/admin/reimbursements/employers'),
          request.get('/admin/reimbursements/groups'),
          request.get('/admin/reimbursements/prefixes'),
        ]);

        this.payers = payersRes.data.payers || [];
        this.employers = employersRes.data.employers || [];
        this.groups = groupsRes.data.groups || [];
        this.prefixes = prefixesRes.data.prefixes || [];

        // Add "No [Entity]" options for explicit null selection
        this.payerOptions = [{ id: null, name: '-- No Payer --' }, ...this.payers];
        this.employerOptions = [{ id: null, name: '-- No Employer --' }, ...this.employers];
        this.groupOptions = [{ id: null, number: '-- No Group --' }, ...this.groups];
        this.prefixOptions = [{ id: null, prefix: '-- No Prefix --' }, ...this.prefixes];
      } catch (error) {
        console.error('Error loading dropdown data:', error);
        this.$buefy.toast.open({
          message: 'Failed to load dropdown options',
          type: 'is-warning',
        });
      }
    },

    // Handler methods for autocomplete selections
    handlePayerSelect(option) {
      if (option) {
        this.editForm.payer_id = option.id;
        this.editForm.payer_name = option.name;
      } else {
        this.editForm.payer_id = null;
        this.editForm.payer_name = '';
      }
    },

    handleEmployerSelect(option) {
      if (option) {
        this.editForm.employer_id = option.id;
        this.editForm.employer_name = option.name;
      } else {
        this.editForm.employer_id = null;
        this.editForm.employer_name = '';
      }
    },

    handleGroupSelect(option) {
      if (option) {
        this.editForm.group_id = option.id;
        this.editForm.group_number = option.number;
      } else {
        this.editForm.group_id = null;
        this.editForm.group_number = '';
      }
    },

    handlePrefixSelect(option) {
      if (option) {
        this.editForm.prefix_id = option.id;
        this.editForm.prefix_code = option.prefix;
      } else {
        this.editForm.prefix_id = null;
        this.editForm.prefix_code = '';
      }
    },

    async openEditModal(reimbursement) {
      this.editingReimbursement = reimbursement;

      // Ensure dropdown data is loaded before opening modal
      if (this.payerOptions.length === 0) {
        await this.loadDropdownData();
      }

      // Convert date to Date object for datepicker
      let paymentDate = null;
      if (reimbursement.payment_date) {
        paymentDate = new Date(reimbursement.payment_date);
      }

      // Initialize form with basic values and text values for autocomplete fields
      this.editForm = {
        charge_claim_id: reimbursement.charge_claim_id || '',
        revenue_code: reimbursement.revenue_code || '',
        charge_billed_cpt_code: reimbursement.charge_billed_cpt_code || '',
        // IDs for actual submission
        payer_id: reimbursement.payer?.id || null,
        employer_id: reimbursement.employer?.id || null,
        group_id: reimbursement.group?.id || null,
        prefix_id: reimbursement.prefix?.id || null,
        // Text values for autocomplete fields
        payer_name: reimbursement.payer?.name || '',
        employer_name: reimbursement.employer?.name || '',
        group_number: reimbursement.group?.number || '',
        prefix_code: reimbursement.prefix?.prefix || '',
        primary_member_id: reimbursement.primary_member_id || '',
        payment_date: paymentDate,
        percent_allowed_on_charges: reimbursement.percent_allowed_on_charges || 0,
        charge_debit_amount: reimbursement.charge_debit_amount || 0,
        payment_allowed_amount: reimbursement.payment_allowed_amount || 0,
      };

      this.showEditModal = true;
    },

    async saveReimbursement() {
      if (!this.editingReimbursement) return;

      try {
        this.editLoading = true;

        // Handle creating new entities if needed
        await this.ensureEntitiesExist();

        // Prepare the data for the API (excluding text values)
        const updateData = {
          charge_claim_id: this.editForm.charge_claim_id,
          revenue_code: this.editForm.revenue_code,
          charge_billed_cpt_code: this.editForm.charge_billed_cpt_code,
          primary_member_id: this.editForm.primary_member_id,
          payer_id: this.editForm.payer_id,
          employer_id: this.editForm.employer_id,
          group_id: this.editForm.group_id,
          prefix_id: this.editForm.prefix_id,
          percent_allowed_on_charges: this.editForm.percent_allowed_on_charges,
          charge_debit_amount: this.editForm.charge_debit_amount,
          payment_allowed_amount: this.editForm.payment_allowed_amount,
        };

        // Convert date to ISO string if present
        if (this.editForm.payment_date) {
          updateData.payment_date = this.editForm.payment_date.toISOString();
        }

        console.log('Sending update data:', updateData);

        const response = await request.put(
          `/admin/reimbursements/${this.editingReimbursement.id}`,
          updateData
        );

        this.$buefy.toast.open({
          message: response.data.message || 'Reimbursement updated successfully',
          type: 'is-success',
        });

        this.showEditModal = false;
        this.editingReimbursement = null;
        this.editForm = {};

        // Refresh the list and dropdown data
        await Promise.all([
          this.loadReimbursements(),
          this.loadDropdownData(), // Refresh to include any newly created entities
        ]);
      } catch (error) {
        console.error('Error updating reimbursement:', error);

        let message = 'Failed to update reimbursement';
        if (error.response && error.response.data && error.response.data.error) {
          message = error.response.data.error;
        }

        this.$buefy.toast.open({
          message: message,
          type: 'is-danger',
        });
      } finally {
        this.editLoading = false;
      }
    },

    async ensureEntitiesExist() {
      // Handle Payer
      if (this.editForm.payer_name && this.editForm.payer_name.trim()) {
        const payerName = this.editForm.payer_name.trim();
        if (payerName !== '-- No Payer --') {
          const existingPayer = this.payers.find(
            (p) => p.name.toLowerCase() === payerName.toLowerCase()
          );
          if (!existingPayer) {
            const newPayer = await this.createNewEntity('payer', { name: payerName });
            this.editForm.payer_id = newPayer.id;
            // Add to local arrays for immediate use
            this.payers.push(newPayer);
            this.payerOptions.push(newPayer);
          } else {
            this.editForm.payer_id = existingPayer.id;
          }
        } else {
          this.editForm.payer_id = null;
        }
      }

      // Handle Employer
      if (this.editForm.employer_name && this.editForm.employer_name.trim()) {
        const employerName = this.editForm.employer_name.trim();
        if (employerName !== '-- No Employer --') {
          const existingEmployer = this.employers.find(
            (e) => e.name.toLowerCase() === employerName.toLowerCase()
          );
          if (!existingEmployer) {
            const newEmployer = await this.createNewEntity('employer', { name: employerName });
            this.editForm.employer_id = newEmployer.id;
            // Add to local arrays for immediate use
            this.employers.push(newEmployer);
            this.employerOptions.push(newEmployer);
          } else {
            this.editForm.employer_id = existingEmployer.id;
          }
        } else {
          this.editForm.employer_id = null;
        }
      }

      // Handle Group
      if (this.editForm.group_number && this.editForm.group_number.trim()) {
        const groupNumber = this.editForm.group_number.trim();
        if (groupNumber !== '-- No Group --') {
          const existingGroup = this.groups.find(
            (g) => g.number.toLowerCase() === groupNumber.toLowerCase()
          );
          if (!existingGroup) {
            const newGroup = await this.createNewEntity('group', { number: groupNumber });
            this.editForm.group_id = newGroup.id;
            // Add to local arrays for immediate use
            this.groups.push(newGroup);
            this.groupOptions.push(newGroup);
          } else {
            this.editForm.group_id = existingGroup.id;
          }
        } else {
          this.editForm.group_id = null;
        }
      }

      // Handle Prefix
      if (this.editForm.prefix_code && this.editForm.prefix_code.trim()) {
        const prefixCode = this.editForm.prefix_code.trim();
        if (prefixCode !== '-- No Prefix --') {
          const existingPrefix = this.prefixes.find(
            (p) => p.prefix.toLowerCase() === prefixCode.toLowerCase()
          );
          if (!existingPrefix) {
            const newPrefix = await this.createNewEntity('prefix', { prefix: prefixCode });
            this.editForm.prefix_id = newPrefix.id;
            // Add to local arrays for immediate use
            this.prefixes.push(newPrefix);
            this.prefixOptions.push(newPrefix);
          } else {
            this.editForm.prefix_id = existingPrefix.id;
          }
        } else {
          this.editForm.prefix_id = null;
        }
      }
    },

    async createNewEntity(type, data) {
      const endpoints = {
        payer: '/admin/reimbursements/payers',
        employer: '/admin/reimbursements/employers',
        group: '/admin/reimbursements/groups',
        prefix: '/admin/reimbursements/prefixes',
      };

      try {
        const response = await request.post(endpoints[type], data);

        this.$buefy.toast.open({
          message: `Created new ${type}: ${data.name || data.number || data.prefix}`,
          type: 'is-info',
          duration: 3000,
        });

        // Return the created entity - different API responses might have different structures
        return response.data[type] || response.data;
      } catch (error) {
        console.error(`Error creating new ${type}:`, error);
        this.$buefy.toast.open({
          message: `Failed to create new ${type}`,
          type: 'is-warning',
        });
        throw error;
      }
    },
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
}

/* Table styling improvements */
.table th {
  background-color: #f8f9fa !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  color: #495057 !important;
}

.table td {
  vertical-align: middle !important;
}

/* Search section spacing */
.columns {
  margin-bottom: 1rem;
}

.level {
  margin-bottom: 0.75rem;
}

/* Better hover effects */
.table tbody tr:hover {
  background-color: #f8f9fa !important;
}

.table tbody tr.is-selected {
  background-color: #e3f2fd !important;
}

/* Search section styling - matching inquiry table exactly */
.is-expanded {
  flex-grow: 1;
}

.filter-dropdown {
  margin-left: 10px;
}

/* Prevent the background color on selection */
.filter-dropdown .dropdown-item {
  color: black !important;
}

/*Remove background color on hover or active state */
.filter-dropdown .dropdown-item.is-active,
.filter-dropdown .dropdown-item.is-hovered {
  background-color: transparent !important;
}

.checkbox-left {
  display: inline-block;
  padding-left: 10px;
  margin-right: 10px;
}

.filter-dropdown .dropdown-item .b-checkbox {
  display: inline-flex;
  align-items: center;
}

.new-entity-warning {
  font-weight: 600;
  color: #e7c30b;
}
</style>
